import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import { inputLabelClasses } from "@mui/material/InputLabel";
import { darken } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ButtonLoading from "common/components/Elements/ButtonLoading/ButtonLoading";
import { Messages } from "common/components/Elements/ExpandableMessages/ExpandableMessages";
import InputDynamicLabel from "common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel";
import InputUnsetButton from "common/components/Elements/Form/InputUnsetButton/InputUnsetButton";
import type { FileMetaData } from "common/utilities/browser/readFile/readFile";
import { getFileMetadata, gzip, readFile, toBase64 } from "common/utilities/browser/readFile/readFile";
import { generateRandomString } from "common/utilities/core/string";
import useDrag from "common/utilities/hooks/useDrag/useDrag";
import * as I from "core/api/coreApiClient";
import { inputStyles } from "core/utilities/styles/input.styles";
import theme, { sx, ThemeColors } from "core/utilities/theme";
import { filesize } from "filesize";
import type React from "react";
import { useEffect, useRef, useState } from "react";

function FileInfo({ fileName, fileSize }: { fileName?: string; fileSize?: number }) {
  const readableFileSize =
    fileSize !== undefined ? (filesize(fileSize, { base: 2, standard: "jedec" }) as string) : undefined;

  let text = "";
  if (fileName) text += fileName;
  if (readableFileSize) text += text.length === 0 ? readableFileSize : ` (${readableFileSize})`;

  return (
    <Typography
      variant="body2"
      component="span"
      sx={{ display: "inline-block", marginLeft: 1.5, verticalAlign: "middle" }}
    >
      {text}
    </Typography>
  );
}

export type OnChange = (fileMetaData: FileMetaData, data: string | undefined, isEmptyTemplate?: boolean) => void;

export interface ComponentProps {
  label?: string;
  title?: string;
  value?: I.FileMetaData;
  onChange: OnChange;
  onClear?: () => void;
  startUpload?: boolean;
  required?: boolean;
  disabled?: boolean;
  errorMsg?: I.Message[];
  e2eId?: string;
  sideLabel?: boolean;
  modalLabel?: boolean;
  fileTypes: string[];
  gzipData?: boolean;
  loading?: boolean;
  longFormStyle?: boolean;
  helpInfo?: I.Denotation;
  inTable?: boolean;
}

function InputFile({
  label,
  title,
  value,
  onChange,
  startUpload,
  required,
  disabled,
  errorMsg: errorMsgProp,
  e2eId,
  sideLabel,
  modalLabel,
  fileTypes,
  gzipData,
  loading,
  longFormStyle,
  helpInfo: helpInfoProp,
  inTable,
  onClear,
}: ComponentProps) {
  /** Flag used when the user opens an empty template so we don't show default error messages for an empty file */
  const isEmptyTemplateRef = useRef(false);

  const errorMsg = isEmptyTemplateRef.current ? [] : errorMsgProp;

  const helpInfo = helpInfoProp
    ? { ...helpInfoProp, parts: helpInfoProp.parts.filter((p) => p.type !== I.DenotationPartTypeConstants.ModalHTML) }
    : undefined;
  const helpInfoModalHTML = helpInfoProp
    ? { ...helpInfoProp, parts: helpInfoProp.parts.filter((p) => p.type === I.DenotationPartTypeConstants.ModalHTML) }
    : undefined;

  const styles = inputStyles({
    longFormStyle,
    sideLabel,
    modalLabel,
    errorMsg,
    inTable,
  });

  const [id] = useState(() => generateRandomString());
  const [fileMetaData, setFileMetaData] = useState<FileMetaData | undefined>();
  const [uploadProgress, setUploadProgress] = useState({ started: false, progress: 0 });
  const [file, setFile] = useState<File | undefined>();
  const { dragging, onDrop } = useDrag();
  const fileData = useRef<string>();

  useEffect(() => {
    if (startUpload && !uploadProgress.started && file && fileMetaData) {
      isEmptyTemplateRef.current = false;

      readFile(
        file,
        (progress) => setUploadProgress({ started: true, progress }),
        async (data) => {
          fileData.current = gzipData ? toBase64(await gzip(data)) : toBase64(data);

          onChange(fileMetaData, fileData.current);
        },
      );
    }
  }, [onChange, startUpload, uploadProgress.started, file, fileMetaData, gzipData]);

  useEffect(() => {
    if (value && fileMetaData) {
      // Once meta data are present within the value, we can clear the local state
      setFileMetaData(undefined);
    }
  }, [value, fileMetaData]);

  const handleChange = (files: File[] | null) => {
    if (files && files[0]) {
      setFile(files[0]);

      const newMetaData = getFileMetadata(files[0]);

      setFileMetaData(newMetaData);
      isEmptyTemplateRef.current = false;
      onChange(newMetaData, undefined);
      setUploadProgress({ started: false, progress: 0 });
    } else {
      setFile(undefined);
    }
  };

  const handleClear = () => {
    fileData.current = undefined;
    setFile(undefined);
    setFileMetaData(undefined);
    setUploadProgress({ started: false, progress: 0 });

    if (onClear) onClear();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const files: File[] = [];

    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === "file") {
        const itemFile = item.getAsFile();
        if (itemFile) {
          files.push(itemFile);
        }
      }
    });

    if (files.length > 0) {
      handleChange(files);
    }

    onDrop();
  };

  return (
    <InputDynamicLabel
      sideLabel={sideLabel}
      htmlFor={id}
      disabled={disabled}
      errorMsg={errorMsg}
      label={label || ""}
      title={title}
      required={required}
      testid="InputFile"
      inputFileLabel
      inTable={inTable}
    >
      <Box
        sx={sx(
          styles.inputRoot,
          { width: "100%", mt: 0.5 },
          dragging
            ? {
                position: "relative",
                background: ThemeColors.backgroundColor,
                outline: `3px solid ${darken(ThemeColors.backgroundColor, 0.1)}`,
                borderRadius: theme.spacing(0.5),
              }
            : {},
        )}
        onDrop={handleDrop}
      >
        {dragging && (
          <Box sx={{ position: "absolute", top: "calc(50% - 17.5px)", left: "calc(50% - 17.5px)" }}>
            <SaveAltIcon color="primary" fontSize="large" />
          </Box>
        )}
        <label htmlFor={id}>
          {!sideLabel && (
            <Typography
              sx={sx(styles.labelRoot, { display: "inline-block", width: "100%" })}
              component="span"
              color={errorMsg && errorMsg.length > 0 ? "error" : "inherit"}
              className={[errorMsg && errorMsg.length > 0 ? inputLabelClasses.error : "", inputLabelClasses.root].join(
                " ",
              )}
            >
              {label}
            </Typography>
          )}
          <input
            id={id}
            accept={fileTypes.join(",")}
            type="file"
            style={{ display: "none " }}
            onChange={(e) => {
              const target = e.target as HTMLInputElement;
              handleChange(target.files ? Array.from(target.files) : []);
            }}
            // see https://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
            // note that upon same file metadata (filename, mimeType and size) we still don't fire our components onChange event, see above
            onClick={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = "";
            }}
            data-e2e-form-input-file={e2eId}
          />
          <Button
            variant="outlined"
            color={errorMsg && errorMsg.length > 0 ? "error" : "primary"}
            component="span"
            size="small"
            data-e2e-form-input-file-button={e2eId}
          >
            Choose file...
          </Button>

          {helpInfoModalHTML && (
            <FormHelperText error component="div" sx={{ display: "inline-block", ml: 1 }}>
              <Messages denotation={helpInfoModalHTML} />
            </FormHelperText>
          )}
        </label>
        {fileMetaData && <FileInfo fileName={fileMetaData.name} fileSize={fileMetaData.size} />}
        {value && !fileMetaData && <FileInfo fileName={value.fileName} fileSize={value.estimatedFileSizeInBytes} />}
        {uploadProgress.started && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              position: "relative",
              display: "inline-block",
              marginLeft: 1.5,
              verticalAlign: "middle",
              "& span:first-of-type": { marginRight: loading || uploadProgress.progress !== 100 ? 4 : 0 },
            }}
          >
            <span>{loading || uploadProgress.progress !== 100 ? "Uploading..." : "100%"}</span>
            {(loading || uploadProgress.progress !== 100) && (
              <ButtonLoading sx={{ left: undefined, right: 0, marginLeft: 0 }} />
            )}
          </Typography>
        )}
        {onClear && (fileMetaData || value) && !required && (
          <Box sx={{ display: "inline-block", ml: 2 }}>
            <InputUnsetButton onClick={handleClear} />
          </Box>
        )}

        {((errorMsg && errorMsg.length > 0) || helpInfo) && (
          <Box sx={{ width: "100%" }}>
            <FormHelperText error component="div">
              <Messages messages={errorMsg} denotation={helpInfo} />
            </FormHelperText>
          </Box>
        )}
      </Box>
    </InputDynamicLabel>
  );
}

export default InputFile;
